import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Select, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
} from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { useSnackbar } from "notistack";

import ClaimStatusBadge from "../ClaimStatusBadge.jsx";
import NewDamageClaimDialog from "../NewDamageClaimDialog.jsx";
import { getDatum } from "../../services/formatService.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useClaimStates } from "../../hooks/useClaimStates.js";
import { applySortingWithAccessorKeys } from "./utils/tableUtils.js";
import { Button, PageHeader } from "../../theme/components/index.js";
import { colorsFoundation } from "../../theme/index.js";
import {
  buildTablePreset,
  buildRowClickProps,
  buildToolbarControlSx,
} from "./tablePreset.js";

const TABLE_KEY = "claims_full";
const STORAGE_KEY = "v2_table_claims_full";

/**
 * ClaimsListTable — full-width claims list for the Schäden overview page.
 *
 * Mirrors the Klick-Dummy: the page title + subtitle sit on the left (via the
 * shared <PageHeader>), and the primary action plus the search / filter / column
 * controls sit on the right in the page header (not inside a table toolbar). The
 * controls are MRT subcomponents wired to a `useMaterialReactTable` instance,
 * which is the only way to render them outside the table body. All table styling
 * comes from the shared `tablePreset` so this list matches every other table.
 */
export default function ClaimsListTable() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const claimStates = useClaimStates();
  const [searchParams, setSearchParams] = useSearchParams();

  const [scope, setScope] = useState(searchParams.get("scope") || "mine");
  const [modalOpen, setModalOpen] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(() => {
    const filters = {};
    if (scope === "mine") {
      filters.scope = "mine";
    }

    const extraParams = {
      include: ["objekt"],
      sort: [{ field: "updated_at", order: "desc" }],
    };
    if (Object.keys(filters).length > 0) {
      extraParams.filters = filters;
    }

    return [
      {
        key: TABLE_KEY,
        label: "Schäden",
        route: "/data/schadens",
        pageSize: defaultRowsPerPage,
        storageKey: STORAGE_KEY,
        extraParams,
      },
    ];
  }, [scope, defaultRowsPerPage]);

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[TABLE_KEY] || {};
  const rows = tableState.rows || [];
  const totalRows = tableState.totalRows ?? rows.length;
  const pageIndex = tableState.pageIndex ?? 0;
  const pageSize = tableState.pageSize ?? defaultRowsPerPage;
  const isLoading = tableState.isLoading ?? false;

  // (Re)fetch when the scope changes.
  useEffect(() => {
    updateTableState(TABLE_KEY, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const handleChangeScope = (value) => {
    setScope(value);
    setSearchParams(
      (currentParams) => {
        const params = new URLSearchParams(currentParams);
        params.set("scope", value);
        return params;
      },
      { preventScrollReset: true }
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "objekt",
        header: "Objekt",
        size: 360,
        Cell: ({ row }) => {
          const objekt = row.original?.objekt;
          const bezeichnung = objekt?.bezeichnung ?? row.original?.bezeichnung;
          const schadenart = row.original?.schaden_art;
          const mainText =
            bezeichnung ||
            (objekt
              ? `${objekt.strasse || ""} ${objekt.hausnr || ""}`.trim()
              : null) ||
            "Objekt";
          const subtitle =
            schadenart?.label ||
            schadenart ||
            (objekt?.versicherungsart ? objekt.versicherungsart : null);
          return (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {mainText}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "schaden_nummer_makler",
        header: "Schadennummer",
        size: 170,
        Cell: ({ cell }) => (
          <Typography variant="body2">{cell.getValue()}</Typography>
        ),
      },
      {
        accessorKey: "schaden_hoehe",
        header: "Betrag",
        size: 150,
        sortDescFirst: false,
        muiTableHeadCellProps: { align: "right" },
        muiTableBodyCellProps: { align: "right" },
        Cell: ({ cell }) => (
          <Typography variant="body2">
            {cell.getValue()
              ? new Intl.NumberFormat("de-DE", {
                  style: "currency",
                  currency: "EUR",
                }).format(cell.getValue())
              : "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "updated_at",
        header: "Aktualisierung",
        size: 160,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {getDatum(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 180,
        filterVariant: "select",
        filterSelectOptions: claimStates?.states
          ? claimStates.states.map((item) => ({
              value: item.name,
              label: item.label,
            }))
          : [],
        Cell: ({ cell }) => {
          const status = cell.getValue();
          return (
            <ClaimStatusBadge
              status={status?.value || status}
              label={status?.label || status}
              size="small"
            />
          );
        },
      },
    ],
    [claimStates]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: MRT_Localization_DE,
    enableColumnResizing: false,
    enableColumnOrdering: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableSorting: true,
    enableTopToolbar: false, // controls live in the page header instead
    enableBottomToolbar: true,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    manualGlobalFilter: true,
    rowCount: totalRows,
    initialState: { showGlobalFilter: true, density: "comfortable" },
    state: {
      pagination: { pageIndex, pageSize },
      sorting,
      columnFilters,
      globalFilter,
      isLoading,
      showProgressBars: isLoading,
    },
    onPaginationChange: (updater) => {
      const current = { pageIndex, pageSize };
      const next = typeof updater === "function" ? updater(current) : updater;
      if (next.pageSize !== current.pageSize) {
        try {
          window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ rows_per_page: next.pageSize })
          );
        } catch (e) {
          /* ignore */
        }
      }
      updateTableState(TABLE_KEY, {
        pageIndex: next.pageIndex,
        pageSize: next.pageSize,
      });
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      setSorting(next);
      updateTableState(TABLE_KEY, {
        sorting: applySortingWithAccessorKeys(next, columns),
      });
    },
    onColumnFiltersChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(columnFilters) : updater;
      setColumnFilters(next);
      updateTableState(TABLE_KEY, { filters: next });
    },
    onGlobalFilterChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(globalFilter) : updater;
      setGlobalFilter(next ?? "");
      updateTableState(TABLE_KEY, { searchword: next ?? "" });
    },
    // Shared cool-grey table styling + clickable rows (see tablePreset).
    ...buildTablePreset(),
    muiTableBodyRowProps: buildRowClickProps((row) =>
      navigate(`/schaden/${row.original.id}`)
    ),
  });

  return (
    <Box>
      <PageHeader
        title="Schäden"
        subtitle="Übersicht aller gemeldeten Schäden"
        actions={
          <>
            <Select
              value={scope}
              onChange={(e) => handleChangeScope(e.target.value)}
              size="small"
              sx={{
                backgroundColor: colorsFoundation.neutral[100],
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid " + colorsFoundation.neutral[500],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid " + colorsFoundation.key.gold[500],
                },
              }}
            >
              <MenuItem value="mine">Meine Schäden</MenuItem>
              <MenuItem value="all">Alle Schäden</MenuItem>
            </Select>

            <Button
              variant="primary"
              onClick={() => setModalOpen(true)}
              startIcon={<AddIcon sx={{ fontSize: "18px !important" }} />}
              sx={{
                px: "16px",
                py: "9px",
                minHeight: 0,
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: 1.4,
                borderRadius: "8px",
                textTransform: "none",
                whiteSpace: "nowrap",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
                "& .MuiButton-startIcon": { mr: "6px" },
              }}
            >
              Neuer Schaden
            </Button>

            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} sx={buildToolbarControlSx()} />
            <MRT_ShowHideColumnsButton
              table={table}
              sx={buildToolbarControlSx()}
            />
          </>
        }
      />

      <MaterialReactTable table={table} />

      <NewDamageClaimDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
}
